import { useGameBySlug } from '@/api/games';
import { GameID, GameIdNamespace } from '@/enums/gameId';
import { Bet } from '@/models/bet';
import { BetStatusId } from '@/models/bet/status/id';
import { DetailedBet } from '@/models/detailed-bet';
import { ErrorCard, FormGroup, Input } from '@starsoft/common/components';
import { Decimal } from '@starsoft/common/services/Decimal';
import useTranslation from 'next-translate/useTranslation';
import { memo } from 'react';
import BetAmountInput from '../BetAmountInput';
import GamesPannelGameButton from './GameButton';
import { GamePannelProps } from './props';
import GamePannelSkeleton from './Skeleton';
import styles from './styles.module.scss';
function GamePannel({
  children,
  bet,
  gameId,
  inputProps,
  gameButton,
  useSmallPadding,
  hideLastUnifiedBets = false,
  nextMultiplier,
  useOnlyMobileButton,
  isLoading,
  additionalSkeleton,
  error,
  refetch,
  useGridLayout,
}: GamePannelProps) {
  const { t } = useTranslation('common');

  const slug = GameIdNamespace.getGameSlugByGameId(gameId);
  const {
    gameStatus,
    isLoading: isLoadingGameStatus,
    game,
  } = useGameBySlug({
    refetchOnMount: 'always',
    slug,
  });

  const nextBlockTextGames = [GameID.Mines, GameID.Tower];
  const userBet: DetailedBet =
    !!bet && Array.isArray(bet) ? bet[0] : (bet as DetailedBet);

  if (isLoading || isLoadingGameStatus) {
    return (
      <GamePannelSkeleton
        gameId={gameId}
        inputProps={inputProps}
        additionalSkeleton={additionalSkeleton}
        bet={bet}
        hideLastUnifiedBets={hideLastUnifiedBets}
      />
    );
  }

  if (error && refetch) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  return (
    <div
      className={`${styles.container} ${useSmallPadding ? styles['container--small-padding'] : ''}`}
    >
      <div
        className={useOnlyMobileButton ? '' : styles['container__mobile-only']}
      >
        <GamesPannelGameButton
          status={gameStatus}
          gameButton={gameButton}
          game={game}
          aggregator={game?.aggregator}
        />
      </div>
      <div
        className={`${styles.container__wrapper} ${useGridLayout ? styles['container__wrapper--grid'] : ''}`}
      >
        <BetAmountInput
          bet={!!bet && Array.isArray(bet) ? bet[0] : (bet as Bet)}
          {...inputProps}
        />
        {children}
        {userBet?.status?.id?.toString() === BetStatusId.Open?.toString() &&
          nextMultiplier && (
            <div className={styles.container__bet__data}>
              <FormGroup
                label={
                  nextBlockTextGames.includes(gameId)
                    ? t('next_block_multiplier')
                    : t('next_multiplier')
                }
              >
                <Input
                  value={Decimal.fromSubunits(String(nextMultiplier) ?? '0', {
                    scale: 4,
                  }).toNumberString()}
                  readOnly
                />
              </FormGroup>
            </div>
          )}
        {!useOnlyMobileButton && (
          <div className={styles['container__desktop-only']}>
            <GamesPannelGameButton
              status={gameStatus?.toString()}
              gameButton={gameButton}
              game={game}
              aggregator={game?.aggregator}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(GamePannel);
